import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactElement } from 'react';
import type { ShoppingListItemDto } from '../../api/shoppingTypes.js';
import { ItemRow } from './ItemRow.js';

export interface ItemListProps {
  items: ShoppingListItemDto[];
  onToggle: (id: number, value: boolean) => void;
  onRemove: (id: number) => void;
  onReorder: (id: number, targetIndex: number) => void;
}

export function ItemList({
  items,
  onToggle,
  onRemove,
  onReorder,
}: ItemListProps): ReactElement {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (items.length === 0) {
    return (
      <div
        className="rounded border p-4 text-center text-sm"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text-muted)',
        }}
        data-testid="shopping-empty"
      >
        No items yet. Pick a date range and tap Generate.
      </div>
    );
  }

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    void onReorder(active.id as number, reordered.findIndex((it) => it.id === active.id));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((it) => it.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="flex flex-col" data-testid="shopping-items">
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              onToggle={onToggle}
              onRemove={onRemove}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

interface SortableItemProps {
  item: ShoppingListItemDto;
  onToggle: (id: number, value: boolean) => void;
  onRemove: (id: number) => void;
}

function SortableItem({ item, onToggle, onRemove }: SortableItemProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
    listStyle: 'none',
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`shopping-item-wrap-${item.id}`}
      data-position={item.position}
      data-dragging={isDragging ? 'true' : 'false'}
    >
      <ItemRow
        item={item}
        onToggle={onToggle}
        onRemove={onRemove}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </li>
  );
}
